import {
  ArrowRightIcon,
  ArrowDownIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { Direction, Position } from 'eazychart-core';
import { Tooltip } from 'components/Floating/Tooltip';
import { ListBoxInput } from 'components/Inputs/ListBoxInput';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Switch } from 'components/ui/switch';
import { useAllowedDataKeys } from 'hooks/useAllowedDataKeys';
import { useChartConfig } from 'hooks/useChartConfig';
import { ChartType } from 'interfaces/chart';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  AxisConfigWithSpecifier,
  createTickFormatter,
  getTickFormatSpecifier,
} from 'utils/axis';

type LinearAxisConfig = {
  xAxis?: boolean;
  yAxis?: boolean;
  yLineAxis?: boolean;
};

const LINEAR_AXIS_BY_CHART: Record<ChartType, LinearAxisConfig> = {
  column: {
    yAxis: true,
  },
  bar: {
    xAxis: true,
  },
  pie: {},
  line: {
    xAxis: true,
    yAxis: true,
  },
  scatter: {
    xAxis: true,
    yAxis: true,
  },
  bubble: {
    xAxis: true,
    yAxis: true,
  },
  area: {
    xAxis: true,
    yAxis: true,
  },
  lineColumn: {
    yAxis: true,
  },
};

const POSITION_LABELS: Record<Position, string> = {
  [Position.TOP]: 'Haut',
  [Position.BOTTOM]: 'Bas',
  [Position.LEFT]: 'Gauche',
  [Position.RIGHT]: 'Droite',
};

interface AxisPositionOptionProps {
  position: Position;
  direction: Direction;
  name?: string | '';
}

const AxisPositionOption: React.FC<AxisPositionOptionProps> = ({
  position,
  direction,
  name,
}) => {
  const label = POSITION_LABELS[position] ?? position;
  const prefix = name ? `${name}-` : '';
  const id = `${prefix}axis-${direction}-${position}`;
  return (
    <div className="flex items-center gap-2">
      <RadioGroupItem id={id} value={position} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
};

interface AxisConfigFormProps {
  defaultAxis: AxisConfigWithSpecifier;
  direction: Direction;
  name?: string;
  onChange: (axis: AxisConfigWithSpecifier) => void;
  enableLinearOptions?: boolean;
}

export const AxisForm: React.FC<AxisConfigFormProps> = ({
  defaultAxis,
  direction,
  onChange,
  name,
  enableLinearOptions = false,
}) => {
  const [axis, setAxis] = useState<AxisConfigWithSpecifier>(defaultAxis);
  const [tickFormatSpecifier, setTickFormatSpecifier] = useState<string>(() =>
    getTickFormatSpecifier(defaultAxis.tickFormat)
  );
  const [tickFormatError, setTickFormatError] = useState<string>();
  const [domainInput, setDomainInput] = useState<string>(() =>
    Array.isArray(defaultAxis.domain)
      ? defaultAxis.domain.map((value) => String(value)).join(', ')
      : ''
  );
  const [domainError, setDomainError] = useState<string>();
  const { domainKeyOptions } = useAllowedDataKeys();
  const updateAxis = (config: Partial<AxisConfigWithSpecifier>) => {
    const newAxis = {
      ...axis,
      ...config,
    };
    setAxis(newAxis);
    onChange(newAxis);
  };

  useEffect(() => {
    setAxis(defaultAxis);
    const resolvedSpecifier =
      getTickFormatSpecifier(defaultAxis.tickFormat) ||
      defaultAxis.tickFormatSpecifier ||
      '';
    setTickFormatSpecifier(resolvedSpecifier);
    if (
      defaultAxis.tickFormatSpecifier &&
      !defaultAxis.tickFormat &&
      resolvedSpecifier !== ''
    ) {
      setTickFormatError('Spécificateur de format D3 invalide');
    } else {
      setTickFormatError(undefined);
    }
    setDomainInput(
      Array.isArray(defaultAxis.domain)
        ? defaultAxis.domain.map((value) => String(value)).join(', ')
        : ''
    );
    setDomainError(undefined);
  }, [defaultAxis]);

  const prefix = name ? `${name}-` : '';
  const tickCountId = `${prefix}axis-${direction}-tick-count`;
  const tickSizeId = `${prefix}axis-${direction}-tick-size`;
  const tickLengthId = `${prefix}axis-${direction}-tick-length`;
  const tickFormatId = `${prefix}axis-${direction}-tick-format`;
  const domainId = `${prefix}axis-${direction}-domain`;
  const maxPaddingId = `${prefix}axis-${direction}-max-padding`;
  const minPaddingId = `${prefix}axis-${direction}-min-padding`;
  const maxId = `${prefix}axis-${direction}-max`;
  const minId = `${prefix}axis-${direction}-min`;
  const softMaxId = `${prefix}axis-${direction}-soft-max`;
  const softMinId = `${prefix}axis-${direction}-soft-min`;
  const roundRangeId = `${prefix}axis-${direction}-round-range`;
  const clampId = `${prefix}axis-${direction}-clamp`;

  const handleNumericUpdate = (
    key:
      | 'tickCount'
      | 'tickSize'
      | 'tickLength'
      | 'maxPadding'
      | 'minPadding'
      | 'max'
      | 'min'
      | 'softMax'
      | 'softMin',
    value: string
  ) => {
    if (value === '') {
      updateAxis({ [key]: undefined } as Partial<AxisConfigWithSpecifier>);
      return;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }
    updateAxis({ [key]: parsed } as Partial<AxisConfigWithSpecifier>);
  };

  const handleTickFormatChange = (value: string) => {
    setTickFormatSpecifier(value);
    if (value === '') {
      setTickFormatError(undefined);
      updateAxis({ tickFormat: undefined, tickFormatSpecifier: undefined });
      return;
    }
    try {
      const formatter = createTickFormatter(value);
      setTickFormatError(undefined);
      updateAxis({
        tickFormat: formatter,
        tickFormatSpecifier: value,
      });
    } catch (error) {
      setTickFormatError('Spécificateur de format D3 invalide');
    }
  };

  const handleDomainCommit = () => {
    const rawValue = domainInput.trim();
    if (rawValue === '') {
      setDomainError(undefined);
      updateAxis({ domain: undefined });
      return;
    }
    const parsedValues = rawValue
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value !== '')
      .map((value) => Number(value));
    if (
      parsedValues.length < 2 ||
      parsedValues.some((value) => Number.isNaN(value))
    ) {
      setDomainError(
        'Saisissez au moins deux valeurs numériques séparées par des virgules.'
      );
      return;
    }
    setDomainError(undefined);
    updateAxis({ domain: parsedValues });
  };

  const HelpIcon = ({ content }: { content: React.ReactNode }) => (
    <Tooltip content={content} style="light">
      <span
        tabIndex={0}
        aria-label="Plus d'informations"
        className="inline-flex cursor-help items-center text-gray-400 outline-none hover:text-gray-600 focus:text-gray-600"
      >
        <InfoCircledIcon aria-hidden className="h-4 w-4" />
      </span>
    </Tooltip>
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <ListBoxInput<string>
          defaultValue={defaultAxis.domainKey}
          options={domainKeyOptions}
          onChange={(domainKey: string) => {
            updateAxis({
              domainKey,
            });
          }}
        />
      </div>
      <div className="flex flex-row gap-4">
        <Label>Position</Label>
        <RadioGroup
          value={axis.position}
          onValueChange={(value) =>
            updateAxis({
              position: value as Position,
            })
          }
          className="flex flex-row gap-4"
        >
          {direction === Direction.HORIZONTAL ? (
            <>
              <AxisPositionOption
                position={Position.TOP}
                direction={direction}
                name={name}
              />
              <AxisPositionOption
                position={Position.BOTTOM}
                direction={direction}
                name={name}
              />
            </>
          ) : (
            <>
              <AxisPositionOption
                position={Position.LEFT}
                direction={direction}
                name={name}
              />
              <AxisPositionOption
                position={Position.RIGHT}
                direction={direction}
                name={name}
              />
            </>
          )}
        </RadioGroup>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor={tickCountId}>Nombre de graduations</Label>
          <Input
            id={tickCountId}
            type="number"
            min={0}
            value={axis.tickCount ?? ''}
            onChange={(event) =>
              handleNumericUpdate('tickCount', event.target.value)
            }
            placeholder="Auto"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={tickSizeId}>Taille des graduations</Label>
          <Input
            id={tickSizeId}
            type="number"
            min={0}
            value={axis.tickSize ?? ''}
            onChange={(event) =>
              handleNumericUpdate('tickSize', event.target.value)
            }
            placeholder="Auto"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={tickLengthId}>Longueur des graduations</Label>
          <Input
            id={tickLengthId}
            type="number"
            min={0}
            value={axis.tickLength ?? ''}
            onChange={(event) =>
              handleNumericUpdate('tickLength', event.target.value)
            }
            placeholder="Auto"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={tickFormatId}>Format des graduations</Label>
        <Input
          id={tickFormatId}
          value={tickFormatSpecifier}
          onChange={(event) => handleTickFormatChange(event.target.value)}
          placeholder="ex. .2f"
        />
        {tickFormatError && (
          <p className="text-xs text-red-600">{tickFormatError}</p>
        )}
      </div>
      {enableLinearOptions && (
        <>
          <hr />
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor={domainId}>Domaine</Label>
              <HelpIcon content="Remplacez le domaine automatique en définissant des limites numériques explicites séparées par des virgules (ex. 0, 100)." />
            </div>
            <Input
              id={domainId}
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              onBlur={handleDomainCommit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleDomainCommit();
                }
              }}
              placeholder="ex. 0, 100"
            />
            {domainError && (
              <p className="text-xs text-red-600">{domainError}</p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={minPaddingId}>Marge minimale</Label>
                <HelpIcon content="Ajoutez une marge avant le minimum du domaine pour laisser de l'espace au début de l'échelle." />
              </div>
              <Input
                id={minPaddingId}
                type="number"
                value={axis.minPadding ?? ''}
                onChange={(event) =>
                  handleNumericUpdate('minPadding', event.target.value)
                }
                placeholder="Auto"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={maxPaddingId}>Marge maximale</Label>
                <HelpIcon content="Ajoutez une marge après le maximum du domaine pour laisser de l'espace à la fin de l'échelle." />
              </div>
              <Input
                id={maxPaddingId}
                type="number"
                value={axis.maxPadding ?? ''}
                onChange={(event) =>
                  handleNumericUpdate('maxPadding', event.target.value)
                }
                placeholder="Auto"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={minId}>Minimum</Label>
                <HelpIcon content="Définissez une limite inférieure fixe pour l'échelle ; les valeurs en dessous de ce seuil seront rognées ou limitées." />
              </div>
              <Input
                id={minId}
                type="number"
                value={axis.min ?? ''}
                onChange={(event) =>
                  handleNumericUpdate('min', event.target.value)
                }
                placeholder="Auto"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={maxId}>Maximum</Label>
                <HelpIcon content="Définissez une limite supérieure fixe pour l'échelle ; les valeurs au-dessus de ce seuil seront rognées ou limitées." />
              </div>
              <Input
                id={maxId}
                type="number"
                value={axis.max ?? ''}
                onChange={(event) =>
                  handleNumericUpdate('max', event.target.value)
                }
                placeholder="Auto"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={softMinId}>Minimum flexible</Label>
                <HelpIcon content="Suggérez une valeur minimale que l'échelle peut dépasser vers le bas si les données l'exigent." />
              </div>
              <Input
                id={softMinId}
                type="number"
                value={axis.softMin ?? ''}
                onChange={(event) =>
                  handleNumericUpdate('softMin', event.target.value)
                }
                placeholder="Auto"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={softMaxId}>Maximum flexible</Label>
                <HelpIcon content="Suggérez une valeur maximale que l'échelle peut dépasser vers le haut si les données l'exigent." />
              </div>
              <Input
                id={softMaxId}
                type="number"
                value={axis.softMax ?? ''}
                onChange={(event) =>
                  handleNumericUpdate('softMax', event.target.value)
                }
                placeholder="Auto"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex items-center gap-2">
              <Switch
                id={roundRangeId}
                checked={axis.roundRange ?? false}
                onCheckedChange={(checked) =>
                  updateAxis({
                    roundRange: checked,
                  })
                }
              />
              <div className="flex items-center gap-2">
                <Label htmlFor={roundRangeId} className="cursor-pointer">
                  Arrondir la plage
                </Label>
                <HelpIcon content="Arrondissez le domaine à des entiers harmonieux pour garder des valeurs de graduation lisibles." />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id={clampId}
                checked={axis.clamp ?? false}
                onCheckedChange={(checked) =>
                  updateAxis({
                    clamp: checked,
                  })
                }
              />
              <div className="flex items-center gap-2">
                <Label htmlFor={clampId} className="cursor-pointer">
                  Limiter
                </Label>
                <HelpIcon content="Empêchez les valeurs en dehors du domaine de s'afficher au-delà de la plage de l'axe." />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface AxisSectionProps {
  title: string;
  direction: Direction;
  children: React.ReactNode;
  badgeLabel?: string;
}

type DirectionStyle = {
  container: string;
  badge: string;
  title: string;
  Icon: React.ElementType;
};

const AXIS_DIRECTION_STYLES: Record<Direction, DirectionStyle> = {
  [Direction.HORIZONTAL]: {
    container: 'border-sky-100 bg-sky-50 shadow-sm',
    badge: 'border-sky-200 bg-sky-100 text-sky-700',
    title: 'text-sky-900',
    Icon: ArrowRightIcon,
  },
  [Direction.VERTICAL]: {
    container: 'border-emerald-100 bg-emerald-50 shadow-sm',
    badge: 'border-emerald-200 bg-emerald-100 text-emerald-700',
    title: 'text-emerald-900',
    Icon: ArrowDownIcon,
  },
};

const AxisSection: React.FC<AxisSectionProps> = ({
  title,
  direction,
  children,
  badgeLabel,
}) => {
  const {
    container,
    badge,
    title: titleColor,
    Icon,
  } = AXIS_DIRECTION_STYLES[direction];
  const resolvedBadgeLabel =
    badgeLabel ??
    (direction === Direction.HORIZONTAL ? 'Axe horizontal' : 'Axe vertical');

  return (
    <section className={`rounded-lg border p-4 ${container}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3
          className={`text-sm font-semibold uppercase tracking-wide ${titleColor}`}
        >
          {title}
        </h3>
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${badge}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {resolvedBadgeLabel}
        </span>
      </div>
      {children}
    </section>
  );
};

export const AxesForm = () => {
  const {
    chart: {
      props: { xAxis, yAxis, yLineAxis },
      type,
    },
    updateChartProps,
  } = useChartConfig();

  return (
    <form>
      <div className="flex flex-col gap-6">
        <AxisSection direction={Direction.HORIZONTAL} title="Axe horizontal">
          <AxisForm
            defaultAxis={xAxis}
            name={'xAxis'}
            direction={Direction.HORIZONTAL}
            enableLinearOptions={Boolean(LINEAR_AXIS_BY_CHART[type]?.xAxis)}
            onChange={(xAxis) => {
              updateChartProps({
                xAxis,
              });
            }}
          />
        </AxisSection>
        <AxisSection direction={Direction.VERTICAL} title="Axe vertical">
          <AxisForm
            defaultAxis={yAxis}
            name={'yAxis'}
            direction={Direction.VERTICAL}
            enableLinearOptions={Boolean(LINEAR_AXIS_BY_CHART[type]?.yAxis)}
            onChange={(yAxis) => {
              updateChartProps({
                yAxis,
              });
            }}
          />
        </AxisSection>
        {type === 'lineColumn' && (
          <AxisSection
            direction={Direction.VERTICAL}
            title="Axe vertical de la courbe"
            badgeLabel="Axe vertical · Série ligne"
          >
            <AxisForm
              defaultAxis={yLineAxis}
              name={'lineAxis'}
              direction={Direction.VERTICAL}
              enableLinearOptions={Boolean(
                LINEAR_AXIS_BY_CHART[type]?.yLineAxis
              )}
              onChange={(yLineAxis) => {
                updateChartProps({
                  yLineAxis,
                });
              }}
            />
          </AxisSection>
        )}
      </div>
    </form>
  );
};
